import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './AuthContext';
import { sendMessageToGroq } from '../services/groqService';
import { 
  validateConstructionQuery, 
  isFollowUpQuestion, 
  sanitizeInput,
  checkRateLimit,
  getQueryConfidence
} from '../utils/validation';

const ChatContext = createContext(null);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

// ==========================================
// USAGE LIMITS CONFIGURATION
// ==========================================
const LIMITS = {
  // Free tier limits
  FREE_CALCULATIONS_PER_MONTH: 10,
  FREE_FOLLOWUPS_PER_CHAT: 8,           // Max follow-ups per calculation for free users
  FREE_DAILY_API_CALLS: 50,              // Total API calls per day for free users
  
  // Pro tier limits (soft caps to prevent abuse)
  PRO_MONTHLY_SOFT_CAP: 2000,            // Soft limit for Pro users
  PRO_DAILY_SOFT_CAP: 200,               // Daily soft limit for Pro users
  PRO_FOLLOWUPS_PER_CHAT: 50,            // Max follow-ups per calculation for Pro users
  
  // Rate limiting
  COOLDOWN_MS: 2000,                      // 2 seconds between requests
  REQUESTS_PER_MINUTE: 10,                // Max requests per minute
  REQUESTS_PER_HOUR: 100,                 // Max requests per hour (free)
  PRO_REQUESTS_PER_HOUR: 300,             // Max requests per hour (Pro)
};

export const ChatProvider = ({ children }) => {
  const { user, profile } = useAuth();
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [usageData, setUsageData] = useState({ 
    calculation_count: 0, 
    reset_date: null,
    daily_api_calls: 0,
    daily_reset_date: null
  });
  const [anonymousUsage, setAnonymousUsage] = useState(0);
  const [deviceId, setDeviceId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState(null);
  
  // Rate limiting - track request times for minute/hour limits
  const lastRequestTime = useRef(0);
  const requestTimestamps = useRef([]);  // Array of timestamps for rate limiting
  const COOLDOWN_MS = LIMITS.COOLDOWN_MS;
  
  // Pro status - defined early for use in rate limiting callbacks
  const isPro = profile?.is_pro || false;

  // Generate or retrieve device ID for anonymous users
  useEffect(() => {
    let id = localStorage.getItem('device_id');
    if (!id) {
      id = 'device_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('device_id', id);
    }
    setDeviceId(id);
    
    // Load daily usage from localStorage for anonymous users
    const storedDailyUsage = localStorage.getItem('daily_api_calls');
    const storedDailyReset = localStorage.getItem('daily_reset_date');
    if (storedDailyUsage && storedDailyReset) {
      const resetDate = new Date(storedDailyReset);
      if (new Date() < resetDate) {
        setUsageData(prev => ({
          ...prev,
          daily_api_calls: parseInt(storedDailyUsage, 10),
          daily_reset_date: storedDailyReset
        }));
      }
    }
  }, []);
  
  // ==========================================
  // ENHANCED RATE LIMITING
  // ==========================================
  
  /**
   * Check if user can make another request based on rate limits
   * @returns {{ allowed: boolean, reason?: string, waitTime?: number }}
   */
  const checkEnhancedRateLimit = useCallback(() => {
    const now = Date.now();
    
    // Basic cooldown check
    const timeSinceLastRequest = now - lastRequestTime.current;
    if (timeSinceLastRequest < COOLDOWN_MS) {
      return {
        allowed: false,
        reason: 'cooldown',
        waitTime: COOLDOWN_MS - timeSinceLastRequest
      };
    }
    
    // Clean old timestamps (older than 1 hour)
    const oneHourAgo = now - (60 * 60 * 1000);
    requestTimestamps.current = requestTimestamps.current.filter(t => t > oneHourAgo);
    
    // Check requests per minute
    const oneMinuteAgo = now - (60 * 1000);
    const requestsLastMinute = requestTimestamps.current.filter(t => t > oneMinuteAgo).length;
    if (requestsLastMinute >= LIMITS.REQUESTS_PER_MINUTE) {
      return {
        allowed: false,
        reason: 'minute_limit',
        waitTime: 60 * 1000 // Wait a minute
      };
    }
    
    // Check requests per hour
    const maxPerHour = isPro ? LIMITS.PRO_REQUESTS_PER_HOUR : LIMITS.REQUESTS_PER_HOUR;
    const requestsLastHour = requestTimestamps.current.length;
    if (requestsLastHour >= maxPerHour) {
      return {
        allowed: false,
        reason: 'hour_limit',
        waitTime: 60 * 60 * 1000 // Wait an hour
      };
    }
    
    return { allowed: true };
  }, [isPro, COOLDOWN_MS]);
  
  /**
   * Record a request timestamp for rate limiting
   */
  const recordRequest = useCallback(() => {
    const now = Date.now();
    lastRequestTime.current = now;
    requestTimestamps.current.push(now);
  }, []);
  
  /**
   * Check daily API call limits
   * @returns {{ allowed: boolean, remaining: number }}
   */
  const checkDailyLimit = useCallback(() => {
    const now = new Date();
    const dailyResetDate = usageData.daily_reset_date ? new Date(usageData.daily_reset_date) : null;
    
    // Reset daily count if past reset date
    if (!dailyResetDate || now >= dailyResetDate) {
      return { allowed: true, remaining: isPro ? LIMITS.PRO_DAILY_SOFT_CAP : LIMITS.FREE_DAILY_API_CALLS };
    }
    
    const dailyCalls = usageData.daily_api_calls || 0;
    const limit = isPro ? LIMITS.PRO_DAILY_SOFT_CAP : LIMITS.FREE_DAILY_API_CALLS;
    
    return {
      allowed: dailyCalls < limit,
      remaining: Math.max(0, limit - dailyCalls)
    };
  }, [usageData, isPro]);
  
  /**
   * Check follow-up limits for a specific chat
   * @param {object} chat - The chat to check
   * @returns {{ allowed: boolean, remaining: number, reason?: string }}
   */
  const checkFollowUpLimit = useCallback((chat) => {
    if (!chat || !chat.messages) return { allowed: true, remaining: Infinity };
    
    // Count user messages (each user message is a follow-up after the first)
    const userMessages = chat.messages.filter(m => m.role === 'user').length;
    const followUpsUsed = Math.max(0, userMessages - 1); // First message isn't a follow-up
    
    const maxFollowUps = isPro ? LIMITS.PRO_FOLLOWUPS_PER_CHAT : LIMITS.FREE_FOLLOWUPS_PER_CHAT;
    const remaining = Math.max(0, maxFollowUps - followUpsUsed);
    
    if (remaining === 0) {
      return {
        allowed: false,
        remaining: 0,
        reason: `You've reached the maximum of ${maxFollowUps} follow-up questions for this calculation. Start a new calculation to continue.`
      };
    }
    
    return { allowed: true, remaining };
  }, [isPro]);

  // Load data when user changes
  useEffect(() => {
    if (user) {
      loadUserData();
    } else if (deviceId) {
      loadAnonymousData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, deviceId]);

  const loadUserData = async () => {
    if (!user) return;

    try {
      // Load usage
      const { data: usage } = await supabase
        .from('usage_tracking')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      if (usage) {
        setUsageData(usage);
      }

      // Load chats
      const { data: chatsData } = await supabase
        .from('chats')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      setChats(chatsData || []);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadAnonymousData = async () => {
    if (!deviceId) return;

    try {
      const { data } = await supabase
        .from('anonymous_usage')
        .select('calculation_count')
        .eq('device_id', deviceId)
        .single();
      
      if (data) {
        setAnonymousUsage(data.calculation_count);
      }
    } catch (error) {
      console.log('No anonymous usage found');
    }
  };

  const canCalculate = useCallback(() => {
    if (isPro) return true;
    
    if (user) {
      const now = new Date();
      const resetDate = usageData.reset_date ? new Date(usageData.reset_date) : null;
      
      if (!resetDate || now > resetDate) {
        return true; // Will reset on next calculation
      }
      
      return usageData.calculation_count < 10;
    } else {
      return anonymousUsage < 10;
    }
  }, [user, isPro, usageData, anonymousUsage]);

  const getRemainingCalcs = useCallback(() => {
    if (isPro) return Infinity;
    
    if (user) {
      const now = new Date();
      const resetDate = usageData.reset_date ? new Date(usageData.reset_date) : null;
      if (!resetDate || now > resetDate) return 10;
      return Math.max(0, 10 - usageData.calculation_count);
    }
    return Math.max(0, 10 - anonymousUsage);
  }, [user, isPro, usageData, anonymousUsage]);

  const incrementUsage = async () => {
    if (isPro) return; // Pro users don't track usage
    
    if (user) {
      const now = new Date();
      const resetDate = usageData.reset_date ? new Date(usageData.reset_date) : null;
      
      let newCount = usageData.calculation_count + 1;
      let newResetDate = usageData.reset_date;
      
      if (!resetDate || now > resetDate) {
        newCount = 1;
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        newResetDate = nextMonth.toISOString();
      }

      const { data } = await supabase
        .from('usage_tracking')
        .update({ 
          calculation_count: newCount,
          reset_date: newResetDate,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();
      
      if (data) {
        setUsageData(data);
      }
    } else {
      const newCount = anonymousUsage + 1;
      
      await supabase
        .from('anonymous_usage')
        .upsert({
          device_id: deviceId,
          calculation_count: newCount,
          updated_at: new Date().toISOString()
        });
      
      setAnonymousUsage(newCount);
    }
  };

  const detectMaterialType = (message) => {
    const lower = message.toLowerCase();
    if (lower.includes('concrete') || lower.includes('slab') || lower.includes('foundation') || lower.includes('patio')) return 'concrete';
    if (lower.includes('drywall') || lower.includes('sheetrock')) return 'drywall';
    if (lower.includes('paint') || lower.includes('primer')) return 'paint';
    if (lower.includes('floor') || lower.includes('tile') || lower.includes('vinyl') || lower.includes('hardwood') || lower.includes('laminate')) return 'flooring';
    if (lower.includes('lumber') || lower.includes('wood') || lower.includes('2x4') || lower.includes('framing') || lower.includes('deck') || lower.includes('fence')) return 'lumber';
    if (lower.includes('roof') || lower.includes('shingle')) return 'roofing';
    return 'general';
  };

  const extractSummary = (response) => {
    const lines = response.split('\n').filter(l => l.trim());
    return lines.slice(0, 2).join(' ').substring(0, 100) + '...';
  };

  /**
   * Validates user input before sending to AI
   * @param {string} input - User's message
   * @param {boolean} isFollowUp - Whether this is a follow-up message
   * @returns {{ valid: boolean, error?: string }}
   */
  const validateInput = (input, isFollowUp = false) => {
    // Clear previous validation error
    setValidationError(null);

    // Sanitize input
    const sanitized = sanitizeInput(input);
    if (!sanitized) {
      return { valid: false, error: 'Please enter a message.' };
    }

    // Check rate limiting
    const { canRequest, waitTime } = checkRateLimit(lastRequestTime.current, COOLDOWN_MS);
    if (!canRequest) {
      return { 
        valid: false, 
        error: `Please wait ${Math.ceil(waitTime / 1000)} second${waitTime > 1000 ? 's' : ''} before sending another message.` 
      };
    }

    // For follow-up questions, be very lenient - let the AI handle redirection
    if (isFollowUp) {
      return { valid: true, sanitizedInput: sanitized };
    }

    // Check for blocked harmful content only (not off-topic - AI will handle that)
    const validation = validateConstructionQuery(sanitized);
    if (!validation.isValid && validation.reason === 'off_topic') {
      // Check if it's actually harmful content (blocked patterns)
      const harmfulPatterns = [
        /\b(hack|exploit|illegal|weapon|drug|bomb)\b/i,
      ];
      const isHarmful = harmfulPatterns.some(p => p.test(sanitized));
      if (isHarmful) {
        return { valid: false, error: 'I can only help with construction material calculations.' };
      }
      // For non-harmful off-topic, let the AI politely redirect
      return { valid: true, sanitizedInput: sanitized };
    }

    // Check for empty/too short messages
    if (!validation.isValid && (validation.reason === 'empty' || validation.reason === 'too_short')) {
      return { valid: false, error: validation.suggestion };
    }

    // Check for too long messages
    if (!validation.isValid && validation.reason === 'too_long') {
      return { valid: false, error: validation.suggestion };
    }

    // For low confidence queries on new conversations, still allow but with a note
    // The AI will ask for clarification if needed
    const { confidence } = getQueryConfidence(sanitized);
    if (confidence === 'low' && !isFollowUpQuestion(sanitized)) {
      // Allow it through - AI will ask for more details or redirect politely
      return { valid: true, sanitizedInput: sanitized };
    }

    return { valid: true, sanitizedInput: sanitized };
  };

  /**
   * Increment daily API call counter
   */
  const incrementDailyApiCalls = async () => {
    const now = new Date();
    const dailyResetDate = usageData.daily_reset_date ? new Date(usageData.daily_reset_date) : null;
    
    let newDailyCount = (usageData.daily_api_calls || 0) + 1;
    let newDailyResetDate = usageData.daily_reset_date;
    
    // Reset if past reset date or no reset date set
    if (!dailyResetDate || now >= dailyResetDate) {
      newDailyCount = 1;
      // Set reset to tomorrow midnight
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      newDailyResetDate = tomorrow.toISOString();
    }
    
    // Update state
    setUsageData(prev => ({
      ...prev,
      daily_api_calls: newDailyCount,
      daily_reset_date: newDailyResetDate
    }));
    
    // Store in localStorage for anonymous users
    if (!user) {
      localStorage.setItem('daily_api_calls', newDailyCount.toString());
      localStorage.setItem('daily_reset_date', newDailyResetDate);
    }
  };

  /**
   * Check for context drift - if user switches material type mid-conversation
   * @param {string} message - The new message
   * @param {object} chat - The current chat
   * @returns {{ drifted: boolean, newMaterial?: string }}
   */
  const checkContextDrift = (message, chat) => {
    if (!chat || !chat.material_type) return { drifted: false };
    
    const newMaterial = detectMaterialType(message);
    
    // If the new message has a specific material type and it's different from the chat's type
    if (newMaterial !== 'general' && newMaterial !== chat.material_type) {
      return { drifted: true, newMaterial };
    }
    
    return { drifted: false };
  };

  /**
   * Determine if we should use a cheaper model for this request
   * @param {string} input - The user's message
   * @param {boolean} isFollowUp - Whether this is a follow-up
   * @returns {boolean}
   */
  const shouldUseCheaperModel = (input, isFollowUp) => {
    // Use cheaper model for short follow-ups (simple questions)
    if (isFollowUp && input.length < 100) {
      return true;
    }
    return false;
  };

  const sendMessage = async (input, isFollowUp = false, chatId = null, materialContext = null) => {
    // Validate input
    const validation = validateInput(input, isFollowUp);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    const sanitizedInput = validation.sanitizedInput || sanitizeInput(input);
    
    // ==========================================
    // ENHANCED RATE LIMITING CHECKS
    // ==========================================
    
    // Check enhanced rate limits (cooldown, per-minute, per-hour)
    const rateLimitCheck = checkEnhancedRateLimit();
    if (!rateLimitCheck.allowed) {
      if (rateLimitCheck.reason === 'cooldown') {
        throw new Error(`Please wait ${Math.ceil(rateLimitCheck.waitTime / 1000)} seconds before sending another message.`);
      } else if (rateLimitCheck.reason === 'minute_limit') {
        throw new Error('You\'re sending messages too quickly. Please wait a minute before trying again.');
      } else if (rateLimitCheck.reason === 'hour_limit') {
        throw new Error('You\'ve reached the hourly request limit. Please try again later.');
      }
    }
    
    // Check daily API call limit
    const dailyCheck = checkDailyLimit();
    if (!dailyCheck.allowed) {
      throw new Error(`You've reached your daily limit of ${isPro ? LIMITS.PRO_DAILY_SOFT_CAP : LIMITS.FREE_DAILY_API_CALLS} requests. Your limit resets tomorrow.`);
    }
    
    // ==========================================
    // FOLLOW-UP SPECIFIC CHECKS
    // ==========================================
    
    let chatToUpdate;
    let countAsNewCalculation = false;
    
    if (isFollowUp && chatId) {
      chatToUpdate = chats.find(c => c.id === chatId);
      if (!chatToUpdate) throw new Error('Chat not found');
      
      // Check follow-up limit
      const followUpCheck = checkFollowUpLimit(chatToUpdate);
      if (!followUpCheck.allowed) {
        throw new Error(followUpCheck.reason);
      }
      
      // Check for context drift (switching materials)
      const driftCheck = checkContextDrift(sanitizedInput, chatToUpdate);
      if (driftCheck.drifted) {
        // This is essentially a new calculation - count it and warn user
        countAsNewCalculation = true;
        
        // Check if user can start a new calculation
        if (!canCalculate()) {
          throw new Error(`Switching from ${chatToUpdate.material_type} to ${driftCheck.newMaterial} counts as a new calculation. You've reached your monthly limit. Upgrade to Pro for unlimited calculations.`);
        }
      }
    } else {
      // New conversation
      const materialType = materialContext || detectMaterialType(sanitizedInput);
      chatToUpdate = {
        id: crypto.randomUUID(),
        user_id: user?.id,
        messages: [],
        first_message: sanitizedInput,
        material_type: materialType,
        created_at: new Date().toISOString(),
      };
    }

    // Record the request for rate limiting
    recordRequest();
    
    setIsLoading(true);
    setValidationError(null);

    try {
      const userMessage = { role: 'user', content: sanitizedInput };
      const updatedMessages = [...(chatToUpdate.messages || []), userMessage];

      // Determine which model to use (cheaper for simple follow-ups)
      const useCheaperModel = shouldUseCheaperModel(sanitizedInput, isFollowUp);
      
      // Call the AI with model preference
      const aiResponse = await sendMessageToGroq(
        updatedMessages, 
        chatToUpdate.material_type,
        { useCheaperModel }
      );

      updatedMessages.push({ role: 'assistant', content: aiResponse });
      chatToUpdate.messages = updatedMessages;
      chatToUpdate.result_summary = extractSummary(aiResponse);
      chatToUpdate.updated_at = new Date().toISOString();
      
      // Increment daily API call counter
      await incrementDailyApiCalls();

      if (user) {
        if (isFollowUp && !countAsNewCalculation) {
          const { data: updatedChat } = await supabase
            .from('chats')
            .update({
              messages: updatedMessages,
              result_summary: chatToUpdate.result_summary,
              updated_at: chatToUpdate.updated_at
            })
            .eq('id', chatId)
            .select()
            .single();
          
          if (updatedChat) {
            setChats(chats.map(c => c.id === chatId ? updatedChat : c));
          }
        } else {
          const { data: newChat } = await supabase
            .from('chats')
            .insert({
              user_id: user.id,
              material_type: chatToUpdate.material_type,
              first_message: chatToUpdate.first_message,
              result_summary: chatToUpdate.result_summary,
              messages: updatedMessages
            })
            .select()
            .single();
          
          if (newChat) {
            setChats([newChat, ...chats]);
          }
          
          // Count new conversations and context drifts
          await incrementUsage();
        }
      } else {
        if (!isFollowUp || countAsNewCalculation) {
          setChats([chatToUpdate, ...chats]);
          if (!isFollowUp) {
            await incrementUsage();
          }
        }
      }

      return chatToUpdate;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteChat = async (chatId) => {
    if (!user) return;

    try {
      await supabase
        .from('chats')
        .delete()
        .eq('id', chatId)
        .eq('user_id', user.id);
      
      setChats(chats.filter(c => c.id !== chatId));
      if (activeChat?.id === chatId) {
        setActiveChat(null);
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
      throw error;
    }
  };

  const getMaterialIcon = (type) => {
    const icons = {
      concrete: '🧱',
      drywall: '📏',
      paint: '🎨',
      flooring: '🏠',
      lumber: '🪵',
      roofing: '🏗️',
      general: '🔧'
    };
    return icons[type] || '🔧';
  };

  const clearValidationError = () => {
    setValidationError(null);
  };

  /**
   * Get remaining follow-ups for a specific chat
   * @param {object} chat - The chat to check
   * @returns {number}
   */
  const getRemainingFollowUps = useCallback((chat) => {
    const result = checkFollowUpLimit(chat);
    return result.remaining;
  }, [checkFollowUpLimit]);
  
  /**
   * Get limits configuration for UI display
   */
  const getLimitsConfig = useCallback(() => ({
    freeCalculationsPerMonth: LIMITS.FREE_CALCULATIONS_PER_MONTH,
    freeFollowUpsPerChat: LIMITS.FREE_FOLLOWUPS_PER_CHAT,
    freeDailyApiCalls: LIMITS.FREE_DAILY_API_CALLS,
    proFollowUpsPerChat: LIMITS.PRO_FOLLOWUPS_PER_CHAT,
    proDailySoftCap: LIMITS.PRO_DAILY_SOFT_CAP,
  }), []);

  const value = {
    chats,
    activeChat,
    setActiveChat,
    isLoading,
    usageData,
    anonymousUsage,
    deviceId,
    isPro,
    canCalculate,
    getRemainingCalcs,
    getRemainingFollowUps,
    checkFollowUpLimit,
    checkDailyLimit,
    getLimitsConfig,
    sendMessage,
    deleteChat,
    getMaterialIcon,
    detectMaterialType,
    refreshChats: loadUserData,
    validationError,
    clearValidationError,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext;
